import { Repayment, Repayments } from '@/domain/repayments'
import {
  NON_TERMINAL_DATA_SOURCES,
  NON_TERMINAL_DESCRIPTION_TERMS,
  OPTIONAL_TERMINAL_DATA_SOURCES,
} from '@/domain/repayment-validation'
import { Injectable } from '@angular/core'
import { FilterMetadata, LazyLoadEvent } from 'primeng/api'
import { PostgrestSchemaService } from './postgrest-schema.service'

type SortDirection = 'asc' | 'desc'

interface NormalizedFilter extends FilterMetadata {
  field: string
}

export interface RepaymentsQueryState {
  limit: number
  offset: number
  sortField: string
  sortOrder: SortDirection
  filters: NormalizedFilter[]
  globalFilter?: string | null
}

@Injectable({
  providedIn: 'root',
})
export class RepaymentsTableQueryService {
  private static instanceCount = 0
  private readonly instanceId = ++RepaymentsTableQueryService.instanceCount
  private filters: Map<string, NormalizedFilter> = new Map()
  private globalFilter: string | null | undefined
  private globalFilterFields: string[] = []
  private missingRelationFieldsOnly = false
  private sortField = 'repayment_date'
  private sortOrder: SortDirection = 'asc'
  private repaymentDateSecondarySorts: Array<{
    field: string
    order: SortDirection
  }> = []
  private limit = 100
  private offset = 0
  private readonly repaymentsTableName = 'repayments'
  private readonly lastCallMap = new Map<string, number>()

  public readonly defaultSortField = 'repayment_date'
  public readonly defaultSortOrder: SortDirection = 'asc'
  constructor(private readonly schemaService: PostgrestSchemaService) {
    if (this.instanceId > 1) {
      console.warn(
        `[RepaymentsTableQueryService] duplicate instance detected (${this.instanceId})`,
      )
    }
  }

  updateFromLazyLoad(event: LazyLoadEvent): void {
    if (typeof event.first === 'number') {
      this.offset = event.first
    }

    if (typeof event.rows === 'number' && event.rows > 0) {
      this.limit = event.rows
    }

    if (event.sortField) {
      this.sortField = event.sortField
      this.sortOrder = event.sortOrder === 1 ? 'asc' : 'desc'
    } else if (event.sortOrder === undefined) {
      this.sortField = this.defaultSortField
      this.sortOrder = this.defaultSortOrder
    }

    if (event.filters) {
      this.setFilters(event.filters)
    }

    if (event.globalFilter !== undefined) {
      this.globalFilter = this.normalizeFilterInput(event.globalFilter)
    }
  }

  setGlobalFilter(value: string | null | undefined, fields: string[]): void {
    this.globalFilter = this.normalizeFilterInput(value)
    this.globalFilterFields = [...fields]
  }

  setSort(field: string | undefined, order: number | undefined): void {
    if (!field) {
      this.sortField = this.defaultSortField
      this.sortOrder = this.defaultSortOrder
      return
    }

    this.sortField = field
    this.sortOrder = order === 1 ? 'asc' : 'desc'
  }

  setRepaymentDateSecondarySort(
    field: string | null,
    order: SortDirection = 'desc',
  ): void {
    this.setRepaymentDateSecondarySorts(field ? [{ field, order }] : [])
  }

  setRepaymentDateSecondarySorts(
    sorts: Array<{ field: string; order: SortDirection }>,
  ): void {
    this.repaymentDateSecondarySorts = sorts.filter((sort) => !!sort.field)
  }

  setPagination(offset: number, limit: number): void {
    this.offset = Math.max(offset, 0)
    this.limit = limit > 0 ? limit : this.limit
  }

  resetPagination(): void {
    this.offset = 0
  }

  reset(): void {
    this.filters.clear()
    this.globalFilter = undefined
    this.globalFilterFields = []
    this.missingRelationFieldsOnly = false
    this.sortField = this.defaultSortField
    this.sortOrder = this.defaultSortOrder
    this.limit = 100
    this.resetPagination()
  }

  setFilters(filters: {
    [field: string]: FilterMetadata | FilterMetadata[] | undefined
  }): void {
    this.filters.clear()
    Object.entries(filters).forEach(([field, meta]) => {
      if (!meta) {
        return
      }

      if (Array.isArray(meta)) {
        const orValues = meta
          .filter((item) => item && item.operator === 'or')
          .map((item) => item.value)
          .filter((value) => this.isFilterValuePresent(value))
        if (orValues.length > 1) {
          const normalized = this.normalizeFilter(field, {
            value: orValues,
            matchMode: 'in',
            operator: 'or',
          })
          if (normalized) {
            this.filters.set(field, normalized)
            return
          }
        }
      }

      if (!Array.isArray(meta)) {
        const direct = meta as FilterMetadata & {
          constraints?: FilterMetadata[]
        }
        if (this.isFilterValuePresent(direct.value)) {
          const normalized = this.normalizeFilter(field, direct)
          if (normalized) {
            this.filters.set(field, normalized)
            return
          }
        }
      }

      const entries = Array.isArray(meta)
        ? meta
        : Array.isArray(
              (meta as FilterMetadata & { constraints?: FilterMetadata[] })
                .constraints,
            )
          ? (meta as FilterMetadata & { constraints: FilterMetadata[] })
              .constraints
          : [meta]

      const matchModeCandidate = [...entries]
        .reverse()
        .find((entry) => entry?.matchMode)?.matchMode
      let constraintIndex = 0
      for (const item of entries) {
        if (!item) {
          continue
        }
        const normalized = this.normalizeFilter(
          field,
          !item.matchMode && matchModeCandidate
            ? { ...item, matchMode: matchModeCandidate }
            : item,
        )
        if (normalized) {
          // A column-filter menu can contain a range (for example amount <
          // £25 AND amount > £0). Keep every constraint so PostgREST applies
          // the range before pagination instead of silently using only the
          // last rule.
          this.filters.set(`${field}#${constraintIndex}`, normalized)
          constraintIndex += 1
        }
      }
    })
  }

  setFilter(
    field: string,
    metadata: FilterMetadata | FilterMetadata[] | null | undefined,
  ): void {
    if (!metadata) {
      this.filters.delete(field)
      return
    }

    if (!Array.isArray(metadata)) {
      const direct = metadata as FilterMetadata & {
        constraints?: FilterMetadata[]
      }
      if (this.isFilterValuePresent(direct.value)) {
        const normalized = this.normalizeFilter(field, direct)
        if (normalized) {
          this.filters.set(field, normalized)
          return
        }
      }
    }

    const entries = Array.isArray(metadata)
      ? metadata
      : Array.isArray(
            (metadata as FilterMetadata & { constraints?: FilterMetadata[] })
              .constraints,
          )
        ? (metadata as FilterMetadata & { constraints: FilterMetadata[] })
            .constraints
        : [metadata]

    const matchModeCandidate = [...entries]
      .reverse()
      .find((entry) => entry?.matchMode)?.matchMode
    for (let index = entries.length - 1; index >= 0; index -= 1) {
      const item = entries[index]
      if (!item) {
        continue
      }
      const normalized = this.normalizeFilter(
        field,
        !item.matchMode && matchModeCandidate
          ? { ...item, matchMode: matchModeCandidate }
          : item,
      )
      if (normalized) {
        this.filters.set(field, normalized)
        return
      }
    }

    this.filters.delete(field)
  }

  removeFilter(field: string): void {
    Array.from(this.filters.entries()).forEach(([key, filter]) => {
      if (key === field || filter.field === field) {
        this.filters.delete(key)
      }
    })
  }

  hasActiveFilters(): boolean {
    return (
      Array.from(this.filters.values()).some(
        (filter) => !this.isDefaultDeletedFilter(filter),
      ) || !!this.globalFilter || this.missingRelationFieldsOnly
    )
  }

  setMissingRelationFieldsFilter(enabled: boolean): void {
    this.missingRelationFieldsOnly = enabled
  }

  hasGlobalFilter(): boolean {
    return !!this.globalFilter
  }

  isDefaultSort(): boolean {
    return (
      this.sortField === this.defaultSortField &&
      this.sortOrder === this.defaultSortOrder
    )
  }

  isDefaultState(): boolean {
    return (
      !this.hasActiveFilters() &&
      !this.hasGlobalFilter() &&
      this.isDefaultSort()
    )
  }

  getQueryState(): RepaymentsQueryState {
    return {
      limit: this.limit,
      offset: this.offset,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      filters: Array.from(this.filters.values()),
      globalFilter: this.globalFilter,
    }
  }

  async buildPostgrestParams(
    baseParams: Record<string, string | undefined | null> = {},
    options?: { includePagination?: boolean; omitFilterFields?: string[] },
  ): Promise<string> {
    this.logRapidCalls('buildPostgrestParams')
    const params = new URLSearchParams()
    const ilikeCompatibleColumns =
      await this.schemaService.getIlikeCompatibleColumns(
        this.repaymentsTableName,
      )

    const order = this.buildOrderParam()

    if (options?.includePagination ?? true) {
      params.set('limit', String(this.limit))
      params.set('offset', String(this.offset))
    }
    if (order) {
      params.set('order', order)
    }

    Object.entries(baseParams)
      .filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      )
      .forEach(([key, value]) => {
        params.set(key, String(value))
      })

    const nonNullFilterFields = new Set<string>()
    const omittedFields = new Set(options?.omitFilterFields ?? [])
    this.filters.forEach((filter) => {
      if (omittedFields.has(filter.field)) {
        return
      }
      const payload = this.toPostgrestFilter(filter, ilikeCompatibleColumns)
      if (!payload) {
        return
      }

      const { key, value, field } = payload
      if (key && value !== undefined) {
        params.append(key, value)
        if (filter.matchMode === 'equals' || filter.matchMode === 'in') {
          const relationField = (() => {
            if (field.startsWith('merchants')) {
              return 'merchants'
            }
            if (field.startsWith('terminals')) {
              return 'terminals'
            }
            if (field.startsWith('cash_advances')) {
              return 'cash_advances'
            }
            return null
          })()
          if (relationField) {
            nonNullFilterFields.add(relationField)
          }
        }
      }
    })
    if (!omittedFields.has('deleted') && !params.has('deleted')) {
      params.set('deleted', 'eq.false')
    }
    if (this.missingRelationFieldsOnly) {
      const terminalApplicableDescription = [
        'description.is.null',
        'description.eq.',
        `and(${NON_TERMINAL_DESCRIPTION_TERMS.map((term) => `description.not.ilike.*${term}*`).join(',')})`,
      ].join(',')
      const terminalApplicableDataSource = [
        'data_source.is.null',
        'data_source.eq.',
        `and(${[...NON_TERMINAL_DATA_SOURCES, ...OPTIONAL_TERMINAL_DATA_SOURCES].map((source) => `data_source.not.ilike.${source}`).join(',')})`,
      ].join(',')
      params.set(
        'or',
        `(description.is.null,description.eq.,merchant_id.is.null,merchant_id.eq.,cash_advance_id.is.null,cash_advance_id.eq.,repayment_date.is.null,payment_amount.is.null,data_source.is.null,data_source.eq.,and(or(${terminalApplicableDescription}),or(${terminalApplicableDataSource}),or(terminal_id.is.null,terminal_id.eq.)))`,
      )
    }
    nonNullFilterFields.forEach((fieldKey) => {
      if (fieldKey) {
        params.append(fieldKey, 'not.is.null')
      }
    })

    if (this.globalFilter && this.globalFilterFields.length > 0) {
      const encoded = this.escapeForIlike(this.globalFilter)
      const clauses = this.globalFilterFields
        .map((field) => {
          const mappedField = this.mapFieldForPostgrest(field)
          if (
            !mappedField ||
            !this.supportsIlike(field, ilikeCompatibleColumns)
          ) {
            return null
          }
          return `${mappedField}.ilike.*${encoded}*`
        })
        .map((clause) => {
          console.log('Global filter clause:', clause)
          return clause
        })
        .filter((clause): clause is string => !!clause)

      if (clauses.length > 0) {
        params.set('or', `(${clauses.join(',')})`)
      }
    }

    const queryString = params.toString()
    return queryString ? `${queryString}` : ''
  }

  applyToDataset(dataset: Repayments): Repayments {
    const { filters, globalFilter } = this.getQueryState()
    const applicableFilters = filters.filter((filter) =>
      this.isFilterValuePresent(filter.value),
    )

    let result = dataset.filter((entry) =>
      this.matchesFilters(entry, applicableFilters),
    )

    if (globalFilter) {
      result = result.filter((entry) =>
        this.matchesGlobalFilter(entry, globalFilter),
      )
    }

    result = this.sortDataset(result)

    return result.slice(this.offset, this.offset + this.limit)
  }

  private matchesFilters(
    entry: Repayment,
    filters: NormalizedFilter[],
  ): boolean {
    return filters.every((filter) =>
      this.matchesFilter(entry, filter.field, filter),
    )
  }

  private matchesGlobalFilter(entry: Repayment, globalFilter: string): boolean {
    if (!this.globalFilterFields.length) {
      return true
    }

    const needle = globalFilter.toLocaleLowerCase()

    return this.globalFilterFields.some((field) => {
      const value = this.extractFieldValue(entry, field)
      if (value === undefined || value === null) {
        return false
      }
      return String(value).toLocaleLowerCase().includes(needle)
    })
  }

  private sortDataset(dataset: Repayments): Repayments {
    const sorted = [...dataset]
    const field = this.sortField || this.defaultSortField
    const direction = this.sortOrder === 'asc' ? 1 : -1

    sorted.sort((a, b) => {
      const aValue = this.extractFieldValue(a, field)
      const bValue = this.extractFieldValue(b, field)
      const primaryComparison = this.compareSortValues(
        aValue,
        bValue,
        this.isDateField(field),
        direction,
      )
      if (primaryComparison !== 0 || field !== 'repayment_date') {
        return primaryComparison
      }

      if (this.repaymentDateSecondarySorts.length > 0) {
        for (const secondarySort of this.repaymentDateSecondarySorts) {
          const secondaryComparison = this.compareSortValues(
            this.extractFieldValue(a, secondarySort.field),
            this.extractFieldValue(b, secondarySort.field),
            false,
            secondarySort.order === 'asc' ? 1 : -1,
          )
          if (secondaryComparison !== 0) {
            return secondaryComparison
          }
        }
        return 0
      }

      const createdDateComparison = this.compareSortValues(
        this.extractFieldValue(a, 'created_date'),
        this.extractFieldValue(b, 'created_date'),
        true,
        direction,
      )
      if (createdDateComparison !== 0) {
        return createdDateComparison
      }

      // Generated adjustments must appear directly after the repayment that
      // produced them.  This mirrors the database ledger calculation and is
      // deliberately independent of the displayed running balance.
      const generatedComparison = this.compareSortValues(
        this.extractFieldValue(a, 'generated_from_repayment_id') ? 1 : 0,
        this.extractFieldValue(b, 'generated_from_repayment_id') ? 1 : 0,
        false,
        direction,
      )
      if (generatedComparison !== 0) {
        return generatedComparison
      }

      return this.compareSortValues(a.id, b.id, false, direction)
    })

    return sorted
  }

  private compareSortValues(
    aValue: unknown,
    bValue: unknown,
    isDateField: boolean,
    direction: number,
  ): number {
    if (aValue === bValue) {
      return 0
    }

    if (aValue === undefined || aValue === null) {
      return -1 * direction
    }

    if (bValue === undefined || bValue === null) {
      return direction
    }

    if (isDateField) {
      const aDate = this.parseDateValue(aValue)
      const bDate = this.parseDateValue(bValue)

      if (aDate !== null && bDate !== null) {
        return (aDate - bDate) * direction
      }
      if (aDate !== null) {
        return -1 * direction
      }
      if (bDate !== null) {
        return direction
      }
    }

    const aNumeric = this.parseNumericValue(aValue)
    const bNumeric = this.parseNumericValue(bValue)
    if (aNumeric !== null && bNumeric !== null) {
      return (aNumeric - bNumeric) * direction
    }

    return String(aValue).localeCompare(String(bValue)) * direction
  }

  private matchesFilter(
    entry: Repayment,
    field: string,
    filter: NormalizedFilter,
  ): boolean {
    const value = this.extractFieldValue(entry, field)
    const filterValue = filter.value

    if (!this.isFilterValuePresent(filterValue)) {
      return true
    }

    const matchMode = filter.matchMode ?? 'contains'

    if (Array.isArray(filterValue)) {
      return this.compareIn(value, filterValue)
    }

    if (filterValue instanceof Date) {
      return this.compareDates(
        value as string | number | Date,
        filterValue,
        matchMode,
      )
    }

    if (typeof filterValue === 'boolean') {
      return this.compareBooleans(Boolean(value), filterValue, matchMode)
    }

    if (typeof filterValue === 'number' || this.isNumeric(filterValue)) {
      return this.compareNumbers(
        this.parseNumericValue(value),
        Number(filterValue),
        matchMode,
      )
    }

    return this.compareStrings(
      String(value ?? ''),
      String(filterValue),
      matchMode,
    )
  }

  private compareStrings(
    candidate: string,
    needle: string,
    matchMode: string,
  ): boolean {
    const source = candidate.toLocaleLowerCase()
    const target = needle.toLocaleLowerCase()

    switch (matchMode) {
      case 'startsWith':
        return source.startsWith(target)
      case 'endsWith':
        return source.endsWith(target)
      case 'equals':
        return source === target
      case 'notEquals':
        return source !== target
      case 'contains':
      default:
        return source.includes(target)
    }
  }

  private compareNumbers(
    candidate: number | null,
    needle: number,
    matchMode: string,
  ): boolean {
    if (candidate === null || Number.isNaN(candidate) || Number.isNaN(needle)) {
      return false
    }

    switch (matchMode) {
      case 'lt':
        return candidate < needle
      case 'lte':
        return candidate <= needle
      case 'gt':
        return candidate > needle
      case 'gte':
        return candidate >= needle
      case 'notEquals':
        return candidate !== needle
      case 'equals':
      default:
        return candidate === needle
    }
  }

  private compareBooleans(
    candidate: boolean,
    needle: boolean,
    matchMode: string,
  ): boolean {
    switch (matchMode) {
      case 'notEquals':
        return candidate !== needle
      case 'equals':
      default:
        return candidate === needle
    }
  }

  private compareDates(
    candidate: string | number | Date,
    needle: Date,
    matchMode: string,
  ): boolean {
    const candidateDate = this.parseDateValue(candidate)

    if (candidateDate === null) {
      return false
    }

    const candidateTime = this.stripTime(new Date(candidateDate)).valueOf()
    const needleTime = this.stripTime(needle).valueOf()

    switch (matchMode) {
      case 'lt':
        return candidateTime < needleTime
      case 'lte':
        return candidateTime <= needleTime
      case 'gt':
        return candidateTime > needleTime
      case 'gte':
        return candidateTime >= needleTime
      case 'notEquals':
        return candidateTime !== needleTime
      case 'equals':
      default:
        return candidateTime === needleTime
    }
  }

  private compareIn(candidate: unknown, values: unknown[]): boolean {
    if (candidate === null || candidate === undefined) {
      return false
    }
    return values.some((value) => String(value) === String(candidate))
  }

  private stripTime(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }

  private isDateField(field: string): boolean {
    const normalizedField = this.normalizeFieldName(field)
    return normalizedField.endsWith('_date') || normalizedField === 'timestamp'
  }

  private parseDateValue(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null
    }

    if (value instanceof Date) {
      const time = value.valueOf()
      return Number.isNaN(time) ? null : time
    }

    if (typeof value !== 'string') {
      return null
    }

    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }

    const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{2}|\d{4})$/)
    if (slashMatch) {
      const day = Number(slashMatch[1])
      const month = Number(slashMatch[2]) - 1
      let year = Number(slashMatch[3])
      if (slashMatch[3].length === 2) {
        year += 2000
      }
      const parsed = new Date(year, month, day)
      const time = parsed.valueOf()
      return Number.isNaN(time) ? null : time
    }

    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (isoMatch) {
      const year = Number(isoMatch[1])
      const month = Number(isoMatch[2]) - 1
      const day = Number(isoMatch[3])
      const parsed = new Date(year, month, day)
      const time = parsed.valueOf()
      return Number.isNaN(time) ? null : time
    }

    const parsed = new Date(trimmed)
    const time = parsed.valueOf()
    return Number.isNaN(time) ? null : time
  }

  private parseNumericValue(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value
    }

    if (typeof value !== 'string') {
      return null
    }

    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }

    const parenNegative = /^\(.*\)$/.test(trimmed)
    let normalized = trimmed.replace(/[^\d.,-]/g, '')
    if (!normalized) {
      return null
    }

    let trailingNegative = false
    if (
      normalized.endsWith('-') &&
      normalized.indexOf('-', 0) === normalized.length - 1
    ) {
      trailingNegative = true
      normalized = normalized.slice(0, -1)
    }

    normalized = normalized.replace(/,/g, '')
    if (!normalized) {
      return null
    }

    const parsed = Number(normalized)
    if (Number.isNaN(parsed)) {
      return null
    }

    if (parenNegative || trailingNegative) {
      return -Math.abs(parsed)
    }
    return parsed
  }

  private extractFieldValue(entry: Repayment, field: string): unknown {
    if (field === 'embedded_balance_zero_first') {
      return this.parseNumericValue(entry.balance) === 0 ? 0 : 1
    }

    const normalizedField = this.normalizeFieldName(field)
    const parts = normalizedField.split('.')

    if (parts.length === 1) {
      return (entry as unknown as Record<string, unknown>)[normalizedField]
    }

    let current: any = entry
    for (const part of parts) {
      if (current == null) {
        return undefined
      }
      current = current[part]
    }
    return current
  }

  private normalizeFieldName(field: string): string {
    const cleaned = field
      .replace('(', '.')
      .replace(')', '')
      .replace('!inner', '')
    const parts = cleaned.split('.')
    if (parts.length === 0) {
      return cleaned
    }
    const [parent, ...rest] = parts
    const mappedParent = (() => {
      switch (parent) {
        case 'merchants':
          return 'merchant'
        case 'terminals':
          return 'terminal'
        case 'cash_advances':
          return 'cash_advance'
        default:
          return parent
      }
    })()
    return [mappedParent, ...rest].join('.')
  }

  private mapFieldForPostgrest(field: string): string {
    const cleaned = field.replace('!inner', '')
    const dotted = cleaned.replace('(', '.').replace(')', '')

    if (dotted.startsWith('merchants.')) {
      return dotted
    }
    if (dotted.startsWith('terminals.')) {
      return dotted
    }
    if (dotted.startsWith('cash_advances.')) {
      return dotted
    }
    return dotted
  }

  private mapFieldForPostgrestOrder(field: string): string {
    const cleaned = field.replace('!inner', '')
    if (cleaned.includes('(')) {
      return cleaned
    }

    const dotted = cleaned.replace('(', '.').replace(')', '')
    if (dotted.startsWith('merchants.')) {
      const remainder = dotted.replace('merchants.', '')
      return `merchants(${remainder})`
    }
    if (dotted.startsWith('terminals.')) {
      const remainder = dotted.replace('terminals.', '')
      return `terminals(${remainder})`
    }
    if (dotted.startsWith('cash_advances.')) {
      const remainder = dotted.replace('cash_advances.', '')
      return `cash_advances(${remainder})`
    }
    return dotted
  }

  private buildOrderParam(): string {
    const mappedSortField = this.mapFieldForPostgrestOrder(this.sortField)
    if (!mappedSortField) {
      return ''
    }

    const primaryOrder = `${mappedSortField}.${this.sortOrder}`
    if (this.sortField !== 'repayment_date') {
      return primaryOrder
    }

    if (this.repaymentDateSecondarySorts.length > 0) {
      const secondaryOrders = this.repaymentDateSecondarySorts.map(
        (secondarySort) =>
          `${this.mapFieldForPostgrestOrder(secondarySort.field)}.${secondarySort.order}`,
      )
      return [primaryOrder, ...secondaryOrders].join(',')
    }

    const generatedOrder =
      this.sortOrder === 'asc'
        ? 'generated_from_repayment_id.asc.nullsfirst'
        : 'generated_from_repayment_id.desc.nullslast'
    return `${primaryOrder},created_date.${this.sortOrder},${generatedOrder},id.${this.sortOrder}`
  }

  private normalizeFilter(
    field: string,
    metadata: FilterMetadata,
  ): NormalizedFilter | null {
    if (!this.isFilterValuePresent(metadata.value)) {
      return null
    }

    const normalized: NormalizedFilter = {
      field,
      value: metadata.value instanceof Date ? metadata.value : metadata.value,
      matchMode: metadata.matchMode,
      operator: metadata.operator,
    }

    return normalized
  }

  private isDefaultDeletedFilter(filter: NormalizedFilter): boolean {
    return (
      filter.field === 'deleted' &&
      filter.matchMode === 'equals' &&
      filter.value === false
    )
  }

  private toPostgrestFilter(
    filter: NormalizedFilter,
    ilikeCompatibleColumns: Set<string>,
  ): {
    key: string
    value: string
    field: string
  } | null {
    const operator = this.resolveOperator(filter.matchMode)
    const preparedValue = this.prepareFilterValue(filter)

    if (
      operator === 'ilike' &&
      !this.supportsIlike(filter.field, ilikeCompatibleColumns)
    ) {
      return null
    }

    const key = this.mapFieldForPostgrest(filter.field)

    return {
      key,
      value: `${operator}.${preparedValue}`,
      field: filter.field,
    }
  }

  private resolveOperator(matchMode: string | undefined): string {
    switch (matchMode) {
      case 'equals':
        return 'eq'
      case 'in':
        return 'in'
      case 'dateIs':
        return 'eq'
      case 'in':
        return 'in'
      case 'notEquals':
        return 'neq'
      case 'dateIsNot':
        return 'neq'
      case 'lt':
        return 'lt'
      case 'dateBefore':
        return 'lt'
      case 'lte':
        return 'lte'
      case 'gt':
        return 'gt'
      case 'dateAfter':
        return 'gt'
      case 'gte':
        return 'gte'
      case 'endsWith':
      case 'startsWith':
      case 'contains':
      default:
        return 'ilike'
    }
  }

  private prepareFilterValue(filter: NormalizedFilter): string {
    const rawValue = filter.value

    if (rawValue === null || rawValue === undefined) {
      return 'null'
    }

    if (Array.isArray(rawValue)) {
      const entries = rawValue
        .map((value) => String(value).trim())
        .filter((value) => value.length > 0)
      return `(${entries.join(',')})`
    }

    if (rawValue instanceof Date) {
      return this.formatDate(rawValue)
    }

    if (typeof rawValue === 'boolean') {
      return rawValue ? 'true' : 'false'
    }

    if (typeof rawValue === 'number' || this.isNumeric(rawValue)) {
      return encodeURIComponent(String(rawValue))
    }

    const prepared = String(rawValue)

    switch (filter.matchMode) {
      case 'startsWith':
        return `${prepared}*`
      case 'endsWith':
        return `*${prepared}`
      case 'equals':
      case 'notEquals':
      case 'lt':
      case 'lte':
      case 'gt':
      case 'gte':
        return prepared
      case 'contains':
      default:
        return `*${prepared}*`
    }
  }

  private escapeForIlike(value: string): string {
    return value.replace(/[%*]/g, '')
  }

  private supportsIlike(
    field: string,
    ilikeCompatibleColumns: Set<string>,
  ): boolean {
    return ilikeCompatibleColumns.has(field)
  }

  private formatDate(value: Date): string {
    const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000)
    return local.toISOString().split('T')[0]
  }

  private logRapidCalls(label: string, thresholdMs = 250) {
    const now = Date.now()
    const last = this.lastCallMap.get(label)
    this.lastCallMap.set(label, now)
    if (last && now - last < thresholdMs) {
      console.warn(`[RepaymentsTableQueryService] rapid call: ${label}`)
    }
  }

  private normalizeFilterInput(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null
    }

    const trimmed = String(value).trim()
    return trimmed.length > 0 ? trimmed : null
  }

  private isNumeric(value: unknown): boolean {
    return !Array.isArray(value) && !Number.isNaN(Number(value))
  }

  private isFilterValuePresent(value: unknown): boolean {
    if (value === null || value === undefined) {
      return false
    }

    if (Array.isArray(value)) {
      return value.length > 0
    }

    if (typeof value === 'string') {
      return value.trim().length > 0
    }

    return true
  }
}
